import React from 'react';
import { Container, Row, Col, Table } from 'react-bootstrap';
import { Bar, Pie } from 'react-chartjs-2';
import _ from 'lodash';
import createGlobe from 'cobe';

import { LiveSignalRProvider } from '../contexts/live/context';
import { View, PageTitle, Jumbotron } from '../components';
import Utils from '../utils';

const WEBAPI_BASE_URL = process.env.REACT_APP_WEBAPI_BASE_URL;
const STATS_CACHE_KEY = 'cached.stats';

const getStatsFromCache = (checkExpires: boolean): any => {
    const json = localStorage.getItem(STATS_CACHE_KEY);

    if (Utils.isNullOrWhitespace(json)) {
        return null;
    }

    try {
        const obj = JSON.parse(json);

        if (checkExpires) {
            const now = new Date();

            if (obj.expires < now.getTime()) {
                return null;
            }
        }

        return obj.stats;
    } catch (e) {
        console.log('Error: ', e);

        return null;
    }
};

const setStatsInCache = (stats: any) => {
    const now = new Date();
    const expires = new Date(now.getTime() + (5 * 60000));

    const cachedExamples = {
        expires: expires.getTime(),
        stats
    };
    const json = JSON.stringify(cachedExamples);

    localStorage.setItem(STATS_CACHE_KEY, json);
};

const getStatsFromHttp = async (): Promise<any> => {
    try {
        const response = await fetch(`${WEBAPI_BASE_URL}/stats`);

        if (!response.ok) {
            return Promise.resolve(null);
        }

        const result = await response.json();

        return Promise.resolve(result);
    } catch (e) {
        console.log('Error: ', e);

        return Promise.resolve(null);
    }
};

const preloadStats = (): any => {
    const cachedStats = getStatsFromCache(false);
    
    return cachedStats;
};

const loadStats = async () => {
    const cachedStats = getStatsFromCache(true);

    if (!_.isNil(cachedStats?.stats)) {
        return Promise.resolve(cachedStats);
    }

    const stats = await getStatsFromHttp();

    if (!_.isNil(stats)) {
        setStatsInCache(stats);
    }

    return Promise.resolve(stats);
};

const barOptions = (label: string): any => {
    return {
        responsive: true,
        scales: {
            x: {
                title: {
                    display: true,
                    text: label
                },
                stacked: true,
                ticks: {
                    autoSkip: false
                }
            },
            y: {
                title: {
                    display: true,
                    text: 'Number of Requests'
                },
                stacked: true
            }
        }       
    };
};

const pieOptions: any = {
    maintainAspectRatio: false,
    responsive: true,
    plugins: {
        title: {
            display: true,
            text: 'Top 5 Countries',
            position: 'bottom'
        },
        legend: {
            position: 'right'
        }
    }
};

let globePhi = 0;
let globeLocations = [];

const StatsView = () => {
    const canvasRef = React.useRef();
    const [state, setState] = React.useState(preloadStats());

    React.useEffect(() => {
        let globe = null;
        let disposed = false;

        (async () => {
            try {
                const stats = await loadStats();

                if (disposed || _.isNil(stats)) {
                    return;
                }

                setState(stats);

                globePhi = 0;
                globeLocations = [];

                globe = createGlobe(canvasRef.current, {
                    devicePixelRatio: 2,
                    width: 400 * 2,
                    height: 400 * 2,
                    offset: [800,0],
                    phi: 0,
                    theta: 0.3,
                    dark: 1,
                    diffuse: 0.8,
                    mapSamples: 16000,
                    mapBrightness: 6,
                    baseColor: [0.3, 0.3, 0.3],
                    markerColor: [0.21, 0.64, 0.92],
                    glowColor: [0.2, 0.2, 0.2],
                    markers: [],
                    opacity: 0.7,
                    onRender(state) {
                        /*
                        // Get the locations from the data
                        state.markers = locations.value.map(location => ({
                            location: [location.latitude, location.longitude],
                            // Set the size of the marker to 0.1 if it's the user's location, otherwise 0.05
                            size: myLocation.value.latitude === location.latitude && myLocation.value.longitude === location.longitude ? 0.1 : 0.05,
                        }))
                        // Rotate the globe
                        state.phi = phi.value
                        phi.value += 0.01
                        */
                        
                        state.markers = _.map(globeLocations, (location) => {
                            return {
                                location: [location.la, location.lo],
                                size: 0.05,
                                color: (location.t !== 6 ? [0.21, 0.64, 0.92] : [1, 0.8, 0.34])
                            };
                        });
                        state.phi = globePhi;

                        globePhi += 0.008;
                    },
                });
            } catch (e) {
                console.error('Error: ', e);
            }
        })();

        return () => {
            if (globe) {         
                globe.destroy();
            }

            disposed = true;
        }
    }, []);

    const handleUpdateRate = (rate: number) => {
        if (process.env.NODE_ENV !== 'production') {
            console.log('Rate: ', rate);
        }
    };

    const handleUpdateLocations = (locations: any) => {
        if (process.env.NODE_ENV !== 'production') {
            console.log('Locations: ', locations);
        }
        
        globeLocations = locations;
    };

    const liveRequestsLayout = React.useMemo(() => {
        return (
            <Row className="mb-5">
                <Col xs={12}>
                    <h3>Live Requests</h3>
                    <hr />
                    <div style={{ height: '400px', minWidth: '400px', backgroundColor: 'black', display: 'flex', justifyContent: 'center', borderRadius: '20px' }}>
                        <canvas ref={canvasRef} style={{ backgroundColor: 'black'}}>
                        </canvas>
                    </div>
                </Col>
            </Row>          
        );
    }, [canvasRef]);

    const requestTotalsLayout = React.useMemo(() => {
        return (
            <Row className="mb-5">
                <Col xs={12}>
                    <h3>Requests Totals</h3>
                    <hr />
                    <Table bordered>
                        <thead>
                            <tr>
                                <td></td>
                                <td><strong>IPv4</strong></td>
                                <td><strong>IPv6</strong></td>
                                <td><strong>Total</strong></td>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><strong>All Time</strong></td>
                                <td>{state?.allIPv4}</td>
                                <td>{state?.allIPv6}</td>
                                <td>{state?.allTotal}</td>
                            </tr>
                            <tr>
                                <td><strong>Current Year</strong></td>
                                <td>{state?.yearIPv4}</td>
                                <td>{state?.yearIPv6}</td>
                                <td>{state?.yearTotal}</td>
                            </tr>
                            <tr>
                                <td><strong>Current Month</strong></td>
                                <td>{state?.monthIPv4}</td>
                                <td>{state?.monthIPv6}</td>
                                <td>{state?.monthTotal}</td>
                            </tr>
                            <tr>
                                <td><strong>Current Day</strong></td>
                                <td>{state?.dayIPv4}</td>
                                <td>{state?.dayIPv6}</td>
                                <td>{state?.dayTotal}</td>
                            </tr>
                        </tbody>
                    </Table>
                </Col>
            </Row>
        );
    }, [state]);

    const lastYearLayout = React.useMemo(() => {
        return (
            <Row className="mb-5">
                <Col xs={12}>
                    <h3>Last Year</h3>
                    <hr />
                </Col>
                <Col lg={7} md={12}>
                    <Bar data={state?.yearBarData} options={barOptions('Month')} />
                </Col>
                <Col lg={5} md={12}>
                    <Pie data={state?.yearPieData} options={pieOptions} width={null} height={null} style={{width: '100%', height: '100%'}} />
                </Col>
            </Row>
        );
    }, [state]);

    const last30DaysLayout = React.useMemo(() => {
        return (
            <Row className="mb-5">
                <Col xs={12}>
                    <h3>Last 30 Days</h3>
                    <hr />
                </Col>
                <Col lg={7} md={12}>
                    <Bar data={state?.thirtyBarData} options={barOptions('Day')} />
                </Col>
                <Col lg={5} md={12}>
                    <Pie data={state?.thirtyPieData} options={pieOptions} width={null} height={null} style={{width: '100%', height: '100%'}} />
                </Col>
            </Row>
        );
    }, [state]);

    const last7DaysLayout = React.useMemo(() => {
        return (
            <Row className="mb-5">
                <Col xs={12}>
                    <h3>Last 7 Days</h3>
                    <hr />
                </Col>
                <Col lg={7} md={12}>
                    <Bar data={state?.sevenBarData} options={barOptions('Weekday')} />
                </Col>
                <Col lg={5} md={12}>
                    <Pie data={state?.sevenPieData} options={pieOptions} width={null} height={null} style={{width: '100%', height: '100%'}} />
                </Col>
            </Row>
        );
    }, [state]);

    const lastDayLayout = React.useMemo(() => {
        return (
            <Row className="mb-5">
                <Col xs={12}>
                    <h3>Last Day</h3>
                    <hr />
                </Col>
                <Col lg={7} md={12}>
                    <Bar data={state?.dayBarData} options={barOptions('Hour')} />
                </Col>
                <Col lg={5} md={12}>
                    <Pie data={state?.dayPieData} options={pieOptions} width={null} height={null} style={{width: '100%', height: '100%'}} />
                </Col>
            </Row>
        );
    }, [state]);

    const lastUpdatedLayout = React.useMemo(() => {
        return (
            <Row>
                <Col xs={12}>
                    <p style={{width: '100%', textAlign: 'center'}}>Last Updated: {state?.lastUpdated}</p>
                </Col>
            </Row>
        );
    }, [state]);

    let statLayouts: any = null;

    if (!_.isNil(state)) {
        statLayouts = (
            <React.Fragment>
                {liveRequestsLayout}
                {requestTotalsLayout}
                {lastYearLayout}
                {last30DaysLayout}
                {last7DaysLayout}
                {lastDayLayout}
                {lastUpdatedLayout}
            </React.Fragment>          
        );
    } else {
        statLayouts = (
            <Row className="mb-5">
                <Col>
                    <p>Loading statistics please wait...</p>
                </Col>
            </Row>
        );
    }

    return (
        <LiveSignalRProvider serverUrl={WEBAPI_BASE_URL + '/signalr-live'} onRate={handleUpdateRate} onLocations={handleUpdateLocations}>
            <View>
                <PageTitle title="Stats" />
                <Jumbotron text="Statistics" subText="Lies, damned lies and statistics." />
                <Container>
                    {statLayouts}
                </Container>
            </View>
        </LiveSignalRProvider>
    );
};

export default StatsView;
